/**
 * 分析三维卡片容器组件
 * 桌面端横向排列，移动端轮播
 * Requirements: 9.1, 9.2, 9.3
 */

import React, { useState, useCallback, useEffect } from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { VisualEvidenceCard } from "./VisualEvidenceCard";
import { RiskAssessmentCard } from "./RiskAssessmentCard";
import { RootCauseCard } from "./RootCauseCard";
import type { InsightTriadProps } from "@/types/conversion-funnel";

/**
 * 分页指示器组件
 */
interface PaginationDotsProps {
  total: number;
  current: number;
  onSelect: (index: number) => void;
}

function PaginationDots({ total, current, onSelect }: PaginationDotsProps) {
  return (
    <div className="flex justify-center gap-2 mt-4">
      {Array.from({ length: total }).map((_, index) => (
        <button
          key={index}
          onClick={() => onSelect(index)}
          className={cn(
            "h-2 rounded-full transition-all duration-200",
            index === current
              ? "w-6 bg-blue-500"
              : "w-2 bg-gray-300 hover:bg-gray-400"
          )}
          aria-label={`Go to slide ${index + 1}`}
        />
      ))}
    </div>
  );
}

/**
 * 分析三维卡片容器
 * 包含视觉证据、风险评估、成因推断三张卡片
 */
export function InsightTriad({
  analysisCard,
  imageUrl,
  className,
}: InsightTriadProps) {
  const { t } = useTranslation("assessments");
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  // 监听轮播变化
  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  // 处理分页点击
  const handleDotClick = useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api]
  );

  // 卡片数据
  const cards = [
    {
      id: "visual-evidence",
      component: (
        <VisualEvidenceCard
          imageUrl={imageUrl || "/placeholder.jpg"}
          conditionDisplay={analysisCard.condition_display}
          confidence={analysisCard.confidence}
          visualEvidence={analysisCard.visual_evidence}
          className="h-full"
        />
      ),
    },
    {
      id: "risk-assessment",
      component: (
        <RiskAssessmentCard
          severity={analysisCard.severity}
          className="h-full"
        />
      ),
    },
    {
      id: "root-cause",
      component: (
        <RootCauseCard
          inference={analysisCard.root_cause_inference}
          className="h-full"
        />
      ),
    },
  ];

  return (
    <div className={cn("w-full", className)}>
      {/* 桌面端：横向排列 */}
      <div className="hidden md:grid md:grid-cols-3 md:gap-4">
        {cards.map((card) => (
          <div key={card.id} className="min-h-[280px]">
            {card.component}
          </div>
        ))}
      </div>

      {/* 移动端：轮播 */}
      <div className="md:hidden">
        <Carousel
          setApi={setApi}
          opts={{
            align: "start",
            loop: false,
          }}
          className="w-full -mx-1"
        >
          <CarouselContent className="-ml-2 px-1">
            {cards.map((card) => (
              <CarouselItem
                key={card.id}
                className="pl-2 basis-[88%] first:ml-0"
              >
                <div className="min-h-[260px]">{card.component}</div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        {/* 分页指示器 */}
        <PaginationDots
          total={cards.length}
          current={current}
          onSelect={handleDotClick}
        />
      </div>
    </div>
  );
}

export default InsightTriad;
